import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty, Field, tableWrap, th, td } from "@/components/common";

const CONFIG = {
  customers: { title: "Customers", subtitle: "Party master for billing, cutting and dispatch.", perm: "parties:write",
    fields: [["name", "Party Name", true], ["gstin", "GSTIN"], ["contact_person", "Contact Person"], ["mobile", "Mobile Number"], ["billing_address", "Billing Address", false, true], ["delivery_address", "Delivery Address", false, true]],
    cols: [["code", "Code"], ["name", "Party Name"], ["gstin", "GSTIN"], ["contact_person", "Contact"], ["mobile", "Mobile"]] },
  suppliers: { title: "Suppliers", subtitle: "Supplier master with purchase history.", perm: "parties:write",
    fields: [["name", "Supplier Name", true], ["gstin", "GSTIN"], ["contact_person", "Contact Person"], ["mobile", "Mobile Number"], ["address", "Address", false, true]],
    cols: [["code", "Code"], ["name", "Supplier"], ["gstin", "GSTIN"], ["contact_person", "Contact"], ["mobile", "Mobile"]] },
  employees: { title: "Employees", subtitle: "Every stock movement records the responsible employee. Login accounts are managed in Administration.", perm: "employees:write",
    fields: [["name", "Employee Name", true], ["mobile", "Mobile Number"], ["department", "Department"], ["designation", "Designation"]],
    cols: [["code", "Employee ID"], ["name", "Name"], ["mobile", "Mobile"], ["department", "Department"], ["designation", "Designation"], ["login_role", "Login Role"]] },
};

function Form({ cfg, kind, initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { status: "active" });
  const save = async () => {
    try {
      const body = Object.fromEntries(cfg.fields.map(([k]) => [k, f[k] || ""]));
      body.status = f.status || "active";
      if (initial?.id) await api.put(`/${kind}/${initial.id}`, body); else await api.post(`/${kind}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid={`${kind}-form-dialog`}>
        <DialogHeader><DialogTitle>{initial?.id ? "Edit" : "Add"} {cfg.title.slice(0, -1).toLowerCase()}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {cfg.fields.map(([k, label, req, area]) => (
            <Field key={k} label={label} required={req} className={area ? "sm:col-span-2" : ""}>
              {area ? <Textarea rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-${k}-input`} />
                : <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: k === "gstin" ? e.target.value.toUpperCase() : e.target.value })} data-testid={`${kind}-${k}-input`} />}
            </Field>
          ))}
          <Field label="Status"><Select value={f.status || "active"} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid={`${kind}-status-select`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">active</SelectItem><SelectItem value="inactive">inactive</SelectItem></SelectContent></Select></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid={`${kind}-save-btn`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Masters({ kind }) {
  const cfg = CONFIG[kind];
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/${kind}`, { params: { q } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle}>
        {can(cfg.perm) && <Button onClick={() => setEdit({})} data-testid={`${kind}-add-btn`}><Plus className="h-4 w-4 mr-1" />Add</Button>}
      </PageHeader>
      <Input placeholder="Search name, code, mobile, GSTIN…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm mb-4" data-testid={`${kind}-search-input`} />
      <div className={tableWrap}>
        <table className="w-full" data-testid={`${kind}-table`}>
          <thead><tr>{cfg.cols.map(([, l]) => <th key={l} className={th}>{l}</th>)}<th className={th}>Status</th><th className={th}></th></tr></thead>
          <tbody>{items.map((i) => (
            <tr key={i.id} className="hover:bg-muted/40" data-testid={`${kind}-row-${i.code}`}>
              {cfg.cols.map(([k]) => <td key={k} className={`${td} ${k === "code" || k === "gstin" ? "font-mono" : ""}`}>{i[k] || "—"}</td>)}
              <td className={td}><StatusBadge status={i.status} /></td>
              <td className={td}>{can(cfg.perm) && <Button size="icon" variant="ghost" onClick={() => setEdit(i)} data-testid={`${kind}-edit-${i.code}`}><Pencil className="h-4 w-4" /></Button>}</td>
            </tr>
          ))}</tbody>
        </table>
        {!items.length && <Empty text={`No ${kind} yet`} />}
      </div>
      {edit && <Form cfg={cfg} kind={kind} initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
